// -----------------------------------------------------------------------------
// ConfirmationPanel  (CONFIRMATION)
//
// Shows the action item's own question/label (item.title — the configured
// prompt, e.g. "Was the ClinCheck sent to the lab?") and Yes / No buttons (or a
// single "Mark complete" when requires_confirmation is false), plus an OPTIONAL
// note. On submit it (1) posts the note to the lead's notes — if one was typed —
// then (2) does the /complete/ POST with the answer, then calls onComplete(item)
// as a refresh signal. The orchestrator must not post completion again.
//
// Note posting mirrors LogCallPanel: POST /leads/{leadId}/notes/ { additional_notes }.
// Completion mirrors ConfirmationCell:
//   POST /api/v1/practices/{practiceId}/tasks/{taskId}/checklist/{itemId}/complete/
//   body { completion_state: 'YES', response_payload: { answer, note? } }
// -----------------------------------------------------------------------------
import PropTypes from 'prop-types';
import { useState } from 'react';
import { Box, Typography, Button, Stack, CircularProgress, TextField } from '@mui/material';
import { dispatch } from 'src/redux/store';
import { updateError } from 'src/redux/slices/user';
import { callApi } from 'src/utils/apiCall';
import Iconify from 'src/components/Iconify';

// Resolve the human question/label from the action item itself — this is the
// "right info" to show, not a generic "Confirm" string.
function resolveQuestion(item) {
    const cfg = item?.raw?.action_config || item?.action_config || {};
    return (
        cfg.label || cfg.question || cfg.prompt || cfg.text
        || item?.title || item?.name || item?.description
        || 'Did you complete this?'
    );
}

export default function ConfirmationPanel({ item, practiceId, leadId, onComplete, onClose }) {
    const [submitting, setSubmitting] = useState(false);
    const [note, setNote] = useState('');

    // requires_confirmation drives the yes/no vs single-button presentation.
    const actionConfig = item?.raw?.action_config || item?.action_config || {};
    const requiresConfirmation = !!actionConfig.requires_confirmation;
    const question = resolveQuestion(item);

    const taskId = item?.taskId ?? item?.rawTask?.id;
    const itemId = item?.itemId ?? item?.id;

    const submit = async (answer) => {
        if (submitting) return;
        if (!practiceId || !taskId || !itemId) {
            // No real task yet (e.g. preview) — just signal the orchestrator.
            onComplete?.(item);
            return;
        }
        setSubmitting(true);
        try {
            // 1) Optional note → lead timeline (best-effort; mirrors LogCallPanel).
            const trimmedNote = note.trim();
            if (trimmedNote && leadId) {
                await callApi(
                    `/api/v1/practices/${practiceId}/leads/${leadId}/notes/`,
                    'POST',
                    { additional_notes: trimmedNote },
                );
            }
            // 2) Complete the checklist item with the answer (+ note echoed in payload).
            const res = await callApi(
                `/api/v1/practices/${practiceId}/tasks/${taskId}/checklist/${itemId}/complete/`,
                'POST',
                { completion_state: 'YES', response_payload: { answer, ...(trimmedNote ? { note: trimmedNote } : {}) } }
            );
            if (res.ok) {
                onComplete?.({ ...item, completionAlreadyPersisted: true });
            } else {
                dispatch(updateError({ success: false, message: res?.data?.detail || 'Failed to confirm — please try again.' }));
            }
        } catch (err) {
            dispatch(updateError({ success: false, message: err?.message || 'Failed to confirm — please try again.' }));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Box
            data-testid="confirmation-panel"
            sx={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                px: 3,
                py: 2.5,
                gap: 1.75,
            }}
        >
            {/* Header: icon + the item's actual question/label */}
            <Stack direction="row" spacing={1.25} alignItems="flex-start">
                <Box
                    sx={{
                        width: 40, height: 40, borderRadius: '11px', flexShrink: 0,
                        bgcolor: 'rgba(0,167,111,0.10)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}
                >
                    <Iconify icon="eva:checkmark-circle-2-outline" width={22} height={22} sx={{ color: '#00A76F' }} />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontSize: '11px', fontWeight: 600, color: '#6e6e73', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                        Confirm
                    </Typography>
                    <Typography data-testid="confirmation-panel-question" sx={{ fontSize: '15px', fontWeight: 700, color: '#1d1d1f', letterSpacing: '-0.01em', lineHeight: 1.3 }}>
                        {question}
                    </Typography>
                </Box>
            </Stack>

            {/* Optional note */}
            <TextField
                data-testid="confirmation-panel-note"
                placeholder="Add a note (optional)"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                disabled={submitting}
                multiline
                minRows={2}
                maxRows={5}
                fullWidth
                size="small"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px', fontSize: '13px' } }}
            />

            <Box sx={{ flex: 1 }} />

            {/* Answer */}
            {requiresConfirmation ? (
                <Stack direction="row" spacing={1}>
                    <Button
                        data-testid="confirmation-panel-no"
                        variant="outlined"
                        fullWidth
                        disabled={submitting}
                        onClick={() => submit('no')}
                        sx={{ textTransform: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: 600 }}
                    >
                        No
                    </Button>
                    <Button
                        data-testid="confirmation-panel-yes"
                        variant="contained"
                        disableElevation
                        fullWidth
                        disabled={submitting}
                        startIcon={submitting ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : null}
                        onClick={() => submit('yes')}
                        sx={{ textTransform: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: 600, bgcolor: '#00A76F', '&:hover': { bgcolor: '#007867' }, boxShadow: 'none' }}
                    >
                        Yes, confirm
                    </Button>
                </Stack>
            ) : (
                <Button
                    data-testid="confirmation-panel-complete"
                    variant="contained"
                    disableElevation
                    fullWidth
                    disabled={submitting}
                    startIcon={submitting ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : <Iconify icon="mdi:check-circle-outline" width={16} height={16} />}
                    onClick={() => submit('yes')}
                    sx={{ textTransform: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: 600, bgcolor: '#00A76F', '&:hover': { bgcolor: '#007867' }, boxShadow: 'none' }}
                >
                    Mark complete
                </Button>
            )}

            <Button
                variant="text"
                size="small"
                disabled={submitting}
                onClick={onClose}
                sx={{ textTransform: 'none', fontSize: '13px', color: '#6e6e73', alignSelf: 'center' }}
            >
                Cancel
            </Button>
        </Box>
    );
}

ConfirmationPanel.propTypes = {
    item: PropTypes.object,
    practiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    leadId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    onComplete: PropTypes.func,
    onClose: PropTypes.func.isRequired,
};

ConfirmationPanel.defaultProps = {
    item: null,
    practiceId: null,
    leadId: null,
    onComplete: null,
};
